import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Waves, Users, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { authFetch, API } from '../utils/api';


export default function ReferralJoin({ user }) {
  const { code } = useParams();
  const [referrer, setReferrer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch(`${API}/api/crew/referral/${code}`);
        if (res.ok) {
          setReferrer(await res.json());
        } else {
          setError('not_found');
        }
      } catch {
        setError('network');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [code]);

  const handleJoin = async () => {
    if (!user) {
      // Not logged in — redirect to landing page to sign in via Stack Auth OTP
      window.location.href = window.location.origin + '/?returnTo=' + encodeURIComponent('/r/' + code);
      return;
    }
    setJoining(true);
    try {
      const res = await authFetch(`${API}/api/crew/referral/${code}`, {
        method: 'POST',
              });
      if (res.ok) {
        setJoined(true);
      }
    } catch {} finally { setJoining(false); }
  };

  const goHome = () => { window.location.href = '/home'; };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center relative z-10">
        <div className="flex space-x-2">
          <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
          <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
          <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 relative z-10">
        <div className="w-full max-w-sm bg-bg-paper/95 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden text-center p-8">
          <Waves className="w-8 h-8 text-primary mx-auto mb-4" />
          <h2 className="font-heading text-lg font-bold mb-2" data-testid="referral-error-title">
            Referral not found
          </h2>
          <p className="text-xs text-muted-foreground mb-6">
            This referral link isn't valid. Check the link and try again.
          </p>
          <a
            href="/"
            className="inline-block bg-primary text-primary-foreground font-bold text-sm py-3 px-8 rounded-2xl hover:opacity-90 transition-opacity"
            data-testid="referral-error-cta"
          >
            Check out SurfSwell.ai
          </a>
        </div>
      </div>
    );
  }

  if (joined) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-sm bg-bg-paper/95 backdrop-blur-xl rounded-3xl border border-primary/30 shadow-2xl overflow-hidden text-center p-8"
          data-testid="referral-joined-card"
        >
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-heading text-xl font-bold mb-2">you're in the crew</h2>
          <p className="text-sm text-muted-foreground mb-6">
            you and <span className="text-foreground font-medium">{referrer.from_name}</span> are now crew
          </p>
          <button
            onClick={goHome}
            data-testid="referral-go-home-btn"
            className="w-full bg-primary text-primary-foreground font-bold text-sm py-3 rounded-2xl hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2"
          >
            let's go
            <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4" data-testid="referral-join-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm overflow-hidden"
      >
        <div className="bg-bg-paper/95 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden">
          {/* Wave Header */}
          <div className="relative h-32 bg-gradient-to-b from-primary/20 to-transparent flex items-center justify-center">
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -bottom-1 left-0 right-0 h-12" style={{
                background: 'radial-gradient(ellipse at 50% 100%, rgba(0,245,255,0.08) 0%, transparent 70%)'
              }} />
            </div>
            <Waves className="w-10 h-10 text-primary relative z-10" strokeWidth={1.5} />
          </div>

          {/* Referrer Info */}
          <div className="px-6 pb-8 text-center -mt-4">
            {referrer.from_picture ? (
              <div className="w-16 h-16 rounded-full overflow-hidden border-4 border-bg-paper mx-auto mb-3 relative z-10">
                <img src={referrer.from_picture} alt="" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary/20 border-4 border-bg-paper mx-auto mb-3 relative z-10 flex items-center justify-center">
                <span className="text-xl font-bold text-primary">
                  {(referrer.from_name || '?')[0].toUpperCase()}
                </span>
              </div>
            )}

            <h1 className="font-heading text-xl font-bold mb-1" data-testid="referral-inviter-name">
              {referrer.from_name || 'A surfer'} invited you
            </h1>
            <p className="text-sm text-muted-foreground mb-1">
              to join the crew on <span className="text-primary font-semibold">SurfSwell.ai</span>
            </p>
            {referrer.from_username && (
              <p className="text-xs text-muted-foreground/60 mb-6">
                @{referrer.from_username}
              </p>
            )}

            <div className="bg-white/5 rounded-2xl p-4 mb-6 text-left">
              <p className="text-xs text-muted-foreground leading-relaxed">
                <span className="text-primary font-semibold">SurfSwell.ai</span> is your AI surf coach and best friend in the lineup.
                Track sessions, get conditions, compare stats with your crew, and never miss a good swell.
              </p>
            </div>

            <button
              onClick={handleJoin}
              disabled={joining}
              data-testid="referral-join-btn"
              className="w-full bg-primary text-primary-foreground font-bold text-sm py-3.5 rounded-2xl hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {joining ? (
                <>joining...</>
              ) : user ? (
                <>
                  <Users className="w-4 h-4" />
                  Join {referrer.from_name?.split(' ')[0] || 'the'}'s crew
                </>
              ) : (
                <>
                  Sign up with Google
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            {!user && (
              <p className="text-[10px] text-muted-foreground/50 mt-3">
                free forever. no credit card needed.
              </p>
            )}
          </div>
        </div>

        <p className="text-center text-[10px] text-muted-foreground/40 mt-4">
          powered by surfswell.ai
        </p>
      </motion.div>
    </div>
  );
}
